import { faKeyboard, faMouse } from '@fortawesome/pro-light-svg-icons';
import { faGamepadAlt as faGamepadAltDuotone } from '@fortawesome/duotone-light-svg-icons/faGamepadAlt';
import { faKeyboard as faKeyboardDuotone } from '@fortawesome/duotone-light-svg-icons/faKeyboard';
import { faMouse as faMouseDuotone } from '@fortawesome/duotone-light-svg-icons/faMouse';
import {
  faBootHeeled,
  faChevronLeft,
  faClose,
  faGamepadAlt as faGamepadAltSolid,
  faLock,
  faPause,
  faPlay,
  faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { InviteButton } from '../../components/widgets/SendInvitePanel';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { PlayerInputOptionsV2 } from '../../components/widgets/PlayerInputOptionsV2';
import { ConnectionType } from '../../components/widgets/Connection';
import { ConnectionV2 } from '../../components/widgets/ConnectionV2';
import { PlaycastGamepadId } from '@playcastdotio/CoreTypes';

type Player = {
  playerId: string;
  name: string;
};

type ControllerPanelProps = {
  name: string;
  playerId?: string;
};

type GamepadPositionButtonsProps = {
  isAfk: boolean;
  isGlobal: boolean;
  playerId?: string;
  buttonClasses: {
    base: string;
    size: string;
    active: string;
    inactive: string;
  };
  handlePositionToggle: (num: number, enabled: boolean) => void;
};

const GamepadPositionButtons = ({ isGlobal, playerId, buttonClasses, handlePositionToggle, isAfk }: GamepadPositionButtonsProps) => {
  const context = useContext(PlaycastContext);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickedNum, setLastClickedNum] = useState<number | null>(null);
  const [showRing, setShowRing] = useState<number | null>(null);

  // Memoize host positions to avoid recalculating for each button
  const hostPositions = useMemo(() => {
    if (isGlobal) return [];
    return context.stateStores.gamepadState.playerMappings.host?.gamepadIndexes ?? [];
  }, [context.stateStores.gamepadState.playerMappings.host?.gamepadIndexes, isGlobal]);

  // Monitor host gamepad changes and unassign positions from players when host takes them
  useEffect(() => {
    if (isGlobal) return; // Only run for player panels

    const playerPositions = context.stateStores.gamepadState.playerMappings[playerId ?? '']?.gamepadIndexes ?? [];

    // Check if any of the player's positions are now taken by host
    playerPositions.forEach((position) => {
      if (hostPositions.includes(position)) {
        // Unassign this position from the player
        handlePositionToggle(position + 1, false);
        // Disable gamepad access if this was their last position
        if (playerPositions.length === 1) {
          context.targetFunctions.setAllowedGamepadInput(false, playerId);
        }
      }
    });
  }, [hostPositions, isGlobal, playerId, handlePositionToggle, context]);

  // Memoize sorted gamepads to avoid sorting on every render
  const sortedPhysicalGamepads = useMemo(() => {
    return context.stateStores.hostGamepadState.gamepads.filter((gamepad) => gamepad.isPhysical).sort((a, b) => a.gamepadId - b.gamepadId);
  }, [context.stateStores.hostGamepadState.gamepads]);

  // Memoize player mappings to avoid recalculating on every render
  const playerMappings = useMemo(() => {
    if (isGlobal) return [];
    return context.stateStores.gamepadState.playerMappings[playerId ?? '']?.gamepadIndexes ?? [];
  }, [context.stateStores.gamepadState.playerMappings, playerId, isGlobal]);

  // Memoize active positions to avoid recalculating for each button
  const activePositions = useMemo(() => {
    if (isGlobal) {
      return sortedPhysicalGamepads.map((gamepad) => gamepad.gamepadId + 1);
    }
    return playerMappings.map((index) => index + 1);
  }, [isGlobal, sortedPhysicalGamepads, playerMappings]);

  // Memoize host gamepad IDs to avoid recalculating for each button
  const hostGamepadIds = useMemo(() => sortedPhysicalGamepads.map((gamepad) => gamepad.gamepadId), [sortedPhysicalGamepads]);

  const handleClick = useCallback(
    (num: number) => {
      const currentTime = Date.now();
      const isActive = activePositions.includes(num);
      const isHostPosition = hostGamepadIds.includes((num - 1) as PlaycastGamepadId);

      // If position is taken by host, don't allow enabling it
      if (!isGlobal && !isActive && isHostPosition) {
        return;
      }

      if (currentTime - lastClickTime < 300 && lastClickedNum === num) {
        // Double click detected
        // Disable all other positions
        [1, 2, 3, 4].forEach((otherNum) => {
          if (otherNum !== num) {
            handlePositionToggle(otherNum, false);
          }
        });
        // Only if it's not taken by host
        if (!isGlobal && !isHostPosition) {
          // Enable the clicked position
          handlePositionToggle(num, true);
          // Ensure gamepad access is enabled
          context.targetFunctions.setAllowedGamepadInput(true, playerId);
          // Show ring animation
          setShowRing(num);
          setTimeout(() => setShowRing(null), 1000);
        }
      } else {
        // Single click
        // Handle gamepad access based on whether this is the last position being disabled
        if (isActive && playerMappings.length === 1) {
          // If we're disabling the last position, disable gamepad access
          context.targetFunctions.setAllowedGamepadInput(false, playerId);
        } else if (!isActive) {
          // If we're enabling any position, ensure gamepad access is enabled
          context.targetFunctions.setAllowedGamepadInput(true, playerId);
        }
        handlePositionToggle(num, !isActive);
      }

      setLastClickTime(currentTime);
      setLastClickedNum(num);
    },
    [
      activePositions,
      context.targetFunctions,
      handlePositionToggle,
      isGlobal,
      lastClickTime,
      lastClickedNum,
      playerId,
      playerMappings.length,
      hostGamepadIds,
    ],
  );

  const positions = useMemo(() => [1, 2, 3, 4], []);

  // Memoize the button component to avoid recreating it on every render
  const GamepadButton = useCallback(
    ({ num }: { num: number }) => {
      const isActive = activePositions.includes(num);
      const isHostPosition = hostGamepadIds.includes((num - 1) as PlaycastGamepadId);
      const isDisabled = !isGlobal && isHostPosition && !isActive;

      const buttonClassName = clsx(
        buttonClasses.base,
        isGlobal ? buttonClasses.size : 'p-1 lg:relative lg:size-9',
        'text-3xl',
        'relative',
        isDisabled ? 'cursor-not-allowed !bg-red-600/30 hover:bg-slate-600' : '',
        isActive ? buttonClasses.active : buttonClasses.inactive,
        isGlobal && !isActive && 'cursor-default hover:!bg-slate-600 active:!bg-slate-600',
        isGlobal && isActive && 'cursor-default hover:!bg-emerald-400 active:!bg-emerald-400',
        isAfk && isActive && 'z-10 bg-purple-900 hover:bg-purple-900/80 active:bg-purple-900/60 text-purple-400',
      );

      return (
        <div key={num} className='relative'>
          {showRing === num && <div className='absolute inset-0 animate-ping rounded-full bg-emerald-400/80' />}
          <button className={buttonClassName} onClick={() => handleClick(num)} disabled={isDisabled}>
            {isDisabled && (
              <FontAwesomeIcon
                icon={faLock}
                className={clsx('text-darkest/80 absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2')}
              />
            )}
            <span
              className={clsx(
                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                isGlobal ? 'text-4xl' : 'text-3xl',
                isDisabled && 'opacity-20',
              )}
            >
              {num}
            </span>
            <FontAwesomeIcon icon={faGamepadAltSolid} className='text-darker/80 size-10 lg:hidden' />
          </button>
        </div>
      );
    },
    [activePositions, hostGamepadIds, isGlobal, buttonClasses, handleClick, showRing],
  );

  return (
    <div className='flex items-center gap-2 px-2 lg:p-0'>
      <FontAwesomeIcon
        icon={isAfk ? faGamepadAltDuotone : faGamepadAltSolid}
        className={clsx(
          'hidden size-12 lg:flex',
          isGlobal ? 'mr-2' : '',
          activePositions.length > 0 ? 'text-emerald-400' : 'text-slate-400',
          isAfk && 'z-10 text-purple-400',
        )}
        style={
          {
            '--fa-secondary-color': '#581c87',
            '--fa-secondary-opacity': '1',
          } as React.CSSProperties
        }
      />
      {positions.map((num) => (
        <GamepadButton key={num} num={num} />
      ))}
    </div>
  );
};

const getConnectionType = (connType: string | undefined): ConnectionType => {
  switch (connType) {
    case 'host':
      return 'Local';
    case 'relay':
      return 'Relay';
    case 'prflx':
    case 'srflx':
      return 'P2P';
    default:
      return 'Unknown';
  }
};

type KeyboardMouseButtonProps = {
  id: string;
  isGlobal?: boolean;
  isKeyboardEnabled: boolean;
  isMouseEnabled: boolean;
  onClick?: () => void;
};

const KeyboardMouseButton = ({ id, isGlobal = false, isKeyboardEnabled, isMouseEnabled, onClick }: KeyboardMouseButtonProps) => {
  return (
    <button
      data-testid={id}
      className={clsx(
        'bg-primary relative flex items-center justify-center rounded-lg p-2',
        isGlobal ? 'active:bg-primary size-12 md:size-16' : 'active:bg-primary/80 size-12',
      )}
      onClick={onClick}
    >
      <FontAwesomeIcon
        icon={faKeyboardDuotone}
        className={clsx('text-darkest group-disabled:text-slate-400', isGlobal ? 'size-10 md:size-12' : 'size-10')}
        style={
          {
            '--fa-secondary-color': isKeyboardEnabled ? '#00d492' : '#2D618C',
            '--fa-secondary-opacity': '1',
          } as React.CSSProperties
        }
      />
      <FontAwesomeIcon
        icon={faMouseDuotone}
        className={clsx(
          'text-darkest absolute right-0 group-disabled:text-slate-400',
          isGlobal ? 'bottom-1 size-6 md:bottom-2 md:size-7' : 'bottom-1 size-6',
        )}
        style={
          {
            '--fa-secondary-color': isMouseEnabled ? '#00d492' : '#2D618C',
            '--fa-secondary-opacity': '1',
          } as React.CSSProperties
        }
      />
    </button>
  );
};

const ControllerPanel = ({ name, playerId }: ControllerPanelProps) => {
  const isGlobal = !playerId;
  const context = useContext(PlaycastContext);
  const [playerStats, setPlayerStats] = useState<{ ping: number; type: ConnectionType }>({ ping: 0, type: 'Unknown' });
  const [players, setPlayers] = useState<Player[]>([]);
  const [isAfk, setIsAfk] = useState(false);

  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [mobileKeyboardAndMouseExpanded, setMobileKeyboardAndMouseExpanded] = useState(false);
  const [mobileGamepadExpanded, setMobileGamepadExpanded] = useState(false);

  const updateStats = useCallback(() => {
    if (!playerId) return;
    const ping = context.stateStores.playerStatState.players[playerId]?.rtt ?? 0;
    const connType = context.stateStores.playerState.players[playerId]?.connectionType;

    setPlayerStats({
      ping: ping * 1000,
      type: getConnectionType(connType),
    });
  }, [context.stateStores.playerStatState.players, context.stateStores.playerState.players, playerId]);

  const updatePlayers = useCallback(() => {
    const playerState = context.stateStores.playerState;
    const out = Object.entries(playerState.players).map(([playerId, player]) => ({
      playerId,
      name: player.displayName,
    }));
    setPlayers(out);
  }, [context.stateStores.playerState]);

  const checkAfk = useCallback(() => {
    if (!playerId) return;
    const afk = context.stateStores.playerActivityState.players[playerId]?.afk;
    setIsAfk(afk);
  }, [context.stateStores.playerActivityState.players, playerId]);

  useEffect(() => {
    if (!playerId) return;
    updateStats();
  }, [playerId, updateStats]);

  useEffect(() => {
    updatePlayers();
  }, [updatePlayers]);

  useEffect(() => {
    checkAfk();
  }, [checkAfk]);

  const handleGamepadToggle = useCallback(() => {
    const isEnabled = context.stateStores.globalInputState.gamepad;
    if (isGlobal) {
      // context.targetFunctions.setAllowedGamepadInput(!isEnabled);
      return;
    } else {
      context.targetFunctions.setAllowedGamepadInput(!isEnabled, playerId);
    }
  }, [context, isGlobal, playerId]);

  const handleKickAll = useCallback(() => {
    context.targetFunctions.kickAll();
  }, [context]);

  const handlePauseAll = useCallback(() => {
    const newPausedState = !context.stateStores.pauseInputState.paused;
    // Update global pause state
    context.targetFunctions.pauseInput(newPausedState);
    // Update all individual player pause states
    players.forEach((player) => {
      context.targetFunctions.pauseInput(newPausedState, player.playerId);
    });
  }, [context, players]);

  const handleKeyboardToggle = useCallback(() => {
    // const enabled = isGlobal
    //   ? !context.stateStores.globalInputState.keyboard
    //   : !context.stateStores.playerState.players[playerId ?? '']?.access.keyboard;
    // if (isGlobal) return context.targetFunctions.setAllowedKeyboardInput(enabled);
    // return context.targetFunctions.setAllowedKeyboardInput(enabled, playerId);
    if (isGlobal) return;
    const enabled = !context.stateStores.playerState.players[playerId ?? '']?.access.keyboard;
    return context.targetFunctions.setAllowedKeyboardInput(enabled, playerId);
  }, [context, isGlobal, playerId]);

  const handleMouseToggle = useCallback(() => {
    // const enabled = isGlobal
    //   ? !context.stateStores.globalInputState.mouse
    //   : !context.stateStores.playerState.players[playerId ?? '']?.access.mouse;
    // if (isGlobal) return context.targetFunctions.setAllowedMouseInput(enabled);
    // return context.targetFunctions.setAllowedMouseInput(enabled, playerId);
    if (isGlobal) return;
    const enabled = !context.stateStores.playerState.players[playerId ?? '']?.access.mouse;
    return context.targetFunctions.setAllowedMouseInput(enabled, playerId);
  }, [context, isGlobal, playerId]);

  const handleKickToggle = useCallback(() => {
    if (isGlobal) return context.targetFunctions.kickAll();
    if (!playerId) return;
    return context.targetFunctions.kickPlayer(playerId);
  }, [context, isGlobal, playerId]);

  const handlePauseToggle = useCallback(() => {
    if (isGlobal) {
      const enabled = !context.stateStores.pauseInputState.paused;
      return context.targetFunctions.pauseInput(!enabled);
    } else {
      // If global pause is active, don't allow unpausing individual players
      if (context.stateStores.pauseInputState.paused) {
        return;
      }
      const enabled = !context.stateStores.pauseInputState.players[playerId ?? '']?.paused;
      return context.targetFunctions.pauseInput(enabled, playerId);
    }
  }, [context, isGlobal, playerId]);

  const handlePositionToggle = (num: number, enabled: boolean) => {
    if (isGlobal) {
      console.error('Cannot enable position in global mode');
    } else {
      if (playerId) {
        context.targetFunctions.setGamepadAssignmentEnabled(playerId, num - 1, enabled);
      }
    }
  };

  const handleMobileExpand = useCallback(() => {
    setMobileExpanded(!mobileExpanded);
  }, [mobileExpanded]);

  const buttonClasses = useMemo(
    () => ({
      base: 'flex items-center justify-center rounded',
      size: 'size-12',
      active: 'bg-emerald-400 hover:bg-emerald-400/80 active:bg-emerald-400/60 text-darkest',
      inactive: 'bg-slate-600 hover:bg-slate-600/80 active:bg-slate-600/60',
    }),
    [],
  );

  const isKeyboardEnabled = useMemo(
    () =>
      isGlobal ? context.stateStores.globalInputState.keyboard : context.stateStores.playerState.players[playerId ?? '']?.access.keyboard,
    [context, isGlobal, playerId],
  );

  const isMouseEnabled = useMemo(
    () => (isGlobal ? context.stateStores.globalInputState.mouse : context.stateStores.playerState.players[playerId ?? '']?.access.mouse),
    [context, isGlobal, playerId],
  );

  const isPaused = useMemo(
    () => (isGlobal ? context.stateStores.pauseInputState.paused : context.stateStores.pauseInputState.players[playerId ?? '']?.paused),
    [context, isGlobal, playerId],
  );

  return (
    <>
      <div className='flex flex-1'>
        <div
          data-testid={`controller-panel-${isGlobal ? 'host' : playerId}`}
          className={clsx(
            'border-primary hidden flex-col justify-between gap-2 rounded-lg border-2 bg-gray-800 p-2 lg:flex',
            !isGlobal && 'w-full',
            !isGlobal && 'relative',
            !isGlobal &&
              isAfk &&
              'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:bg-gray-800/60 after:bg-opacity-50 after:content-[""]',
          )}
        >
          <div className='flex items-center justify-between'>
            <span data-testid={`controller-name-${isGlobal ? 'host' : playerId}`} className='text-white'>
              {name}
            </span>
            {!isGlobal ? <ConnectionV2 ping={playerStats.ping} type={playerStats.type} /> : null}
          </div>
          <div className='flex gap-2'>
            <div
              data-testid={`input-controls-${isGlobal ? 'host' : playerId}`}
              className={clsx(
                'flex flex-1 items-center',
                isGlobal ? 'gap-10' : 'border-primary justify-between gap-2 rounded border-2 p-2',
              )}
            >
              <GamepadPositionButtons
                isAfk={isAfk}
                isGlobal={isGlobal ?? false}
                playerId={playerId}
                buttonClasses={buttonClasses}
                handlePositionToggle={handlePositionToggle}
              />
              <div className='flex gap-2'>
                <button
                  data-testid={`keyboard-toggle-${isGlobal ? 'host' : playerId}`}
                  className={clsx(
                    buttonClasses.base,
                    buttonClasses.size,
                    isGlobal || isKeyboardEnabled ? buttonClasses.active : buttonClasses.inactive,
                    isGlobal && 'cursor-default hover:!bg-emerald-400 active:!bg-emerald-400',
                  )}
                  onClick={handleKeyboardToggle}
                >
                  <FontAwesomeIcon icon={faKeyboard} className='size-8' />
                </button>
                <button
                  data-testid={`mouse-toggle-${isGlobal ? 'host' : playerId}`}
                  className={clsx(
                    buttonClasses.base,
                    buttonClasses.size,
                    isGlobal || isMouseEnabled ? buttonClasses.active : buttonClasses.inactive,
                    isGlobal && 'cursor-default hover:!bg-emerald-400 active:!bg-emerald-400',
                  )}
                  onClick={handleMouseToggle}
                >
                  <FontAwesomeIcon icon={faMouse} className='size-6' />
                </button>
              </div>
            </div>
            {!isGlobal && (
              <div className='flex items-center gap-2'>
                <button
                  data-testid={`kick-button-${name}`}
                  className={clsx('bg-primary active:bg-primary/60 hover:bg-primary/80 rounded', buttonClasses.size)}
                  onClick={handleKickToggle}
                >
                  <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600' />
                </button>
                <button
                  data-testid={`pause-button-${name}`}
                  className={clsx(
                    buttonClasses.base,
                    buttonClasses.size,
                    context.stateStores.pauseInputState.paused
                      ? 'bg-slate-600'
                      : 'bg-primary active:bg-primary/60 hover:bg-primary/80 rounded',
                    'group',
                  )}
                  onClick={handlePauseToggle}
                  disabled={!isGlobal && context.stateStores.pauseInputState.paused}
                >
                  <FontAwesomeIcon icon={isPaused ? faPlay : faPause} className='size-8 text-amber-400 group-disabled:text-slate-400' />
                </button>
                <div
                  data-testid={`afk-button-${name}`}
                  className={clsx(
                    'flex h-12 items-center justify-center rounded px-4 py-1 text-3xl',
                    isAfk ? 'z-10 bg-purple-900 text-purple-400' : 'border-2 border-slate-600 text-slate-600',
                  )}
                >
                  AFK
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile */}
        {isGlobal ? (
          <div data-testid='mobile-host-controls' className='border-primary bg-primary flex w-full gap-2 rounded-lg border-2 lg:hidden'>
            <div className='flex flex-1 items-center justify-between rounded-lg bg-gray-800 p-2'>
              <span className='w-20 text-center'>{name}</span>
              <div className='flex gap-2'>
                <button
                  className={clsx(
                    'bg-primary size-12 items-start justify-center rounded-lg md:size-16',
                    isGlobal ? 'active:bg-primary' : 'active:bg-primary/80',
                  )}
                  onClick={handleGamepadToggle}
                >
                  <FontAwesomeIcon
                    icon={context.stateStores.globalInputState.gamepad ? faGamepadAltDuotone : faGamepadAltSolid}
                    className={clsx('size-10 md:size-12', context.stateStores.globalInputState.gamepad ? 'text-darkest' : 'text-darkest')}
                    style={
                      {
                        '--fa-secondary-color': '#00d492',
                        '--fa-secondary-opacity': '1',
                      } as React.CSSProperties
                    }
                  />
                </button>
                <KeyboardMouseButton
                  id='mobile-keyboard-mouse-button-global'
                  isGlobal
                  isKeyboardEnabled={false}
                  isMouseEnabled={false}
                  onClick={() => console.log('clicked')}
                />
              </div>
            </div>
            <div className='flex items-center gap-2 rounded-lg pr-1.5'>
              <button
                className='flex size-12 items-center justify-center rounded-lg bg-amber-600/40 active:bg-amber-600/60 md:size-16'
                onClick={handlePauseAll}
              >
                <FontAwesomeIcon
                  icon={isPaused ? faPlay : faPause}
                  className='size-8 text-amber-400 group-disabled:text-slate-400 md:size-10'
                />
              </button>
              <button
                className='flex size-12 items-center justify-center rounded-lg bg-orange-600/60 active:bg-orange-600/40 md:size-16'
                onClick={handleKickAll}
              >
                <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600 md:size-8' />
              </button>
            </div>
          </div>
        ) : (
          <div data-testid={`mobile-player-controls-${name}`} className='bg-primary flex w-full rounded-lg p-0.5 lg:hidden'>
            <div className='relative flex w-full items-center gap-1 overflow-hidden rounded-lg'>
              <div className='bg-darkest flex flex-1 flex-col rounded-md px-2 py-0.5'>
                <div>
                  <span className='text-lg text-white'>{name}</span>
                </div>
                <ConnectionV2 ping={playerStats.ping} type={playerStats.type} />
              </div>
              <div
                className={clsx(
                  'bg-darkest absolute right-0 flex h-full w-fit items-center rounded-r-md border-l-4 border-amber-400 transition-all duration-700 ease-in-out',
                  mobileExpanded ? 'translate-x-0' : 'translate-x-[85%]',
                )}
              >
                <div className='flex h-full items-center px-1'>
                  {mobileKeyboardAndMouseExpanded || mobileGamepadExpanded ? (
                    <button
                      onClick={() => {
                        setMobileKeyboardAndMouseExpanded(false);
                        setMobileGamepadExpanded(false);
                      }}
                      className='flex flex-shrink-0 items-center pr-1'
                    >
                      <FontAwesomeIcon icon={faClose} className='size-8 text-amber-400' />
                    </button>
                  ) : (
                    <button onClick={handleMobileExpand} className='flex flex-shrink-0 items-center pr-1'>
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className={clsx(
                          'size-8 text-amber-400 transition-transform duration-700 ease-in-out',
                          mobileExpanded ? 'rotate-180' : 'rotate-0',
                        )}
                      />
                    </button>
                  )}
                  <div className='flex h-full items-center justify-center gap-1 md:gap-2'>
                    {mobileGamepadExpanded ? (
                      <GamepadPositionButtons
                        isAfk={isAfk}
                        isGlobal={false}
                        playerId={playerId}
                        buttonClasses={buttonClasses}
                        handlePositionToggle={handlePositionToggle}
                      />
                    ) : mobileKeyboardAndMouseExpanded ? (
                      <div className='flex items-center gap-2 px-7'>
                        <button
                          className={clsx(
                            'flex size-12 items-center justify-center rounded-lg p-2',
                            isKeyboardEnabled ? 'bg-emerald-400 active:bg-emerald-400/80' : 'bg-primary active:bg-primary/80',
                          )}
                          onClick={handleKeyboardToggle}
                        >
                          <FontAwesomeIcon icon={faKeyboard} className='text-darkest size-10 group-disabled:text-slate-400 md:size-10' />
                        </button>
                        <button
                          className={clsx(
                            'flex size-12 items-center justify-center rounded-lg p-2',
                            isMouseEnabled ? 'bg-emerald-400 active:bg-emerald-400/80' : 'bg-primary active:bg-primary/80',
                          )}
                          onClick={handleMouseToggle}
                        >
                          <FontAwesomeIcon icon={faMouse} className='text-darkest size-8 group-disabled:text-slate-400 md:size-10' />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          data-testid={`mobile-gamepad-button-${playerId}`}
                          className='bg-primary active:bg-primary/80 flex size-12 items-center justify-center rounded-lg p-2'
                          onClick={() => setMobileGamepadExpanded(true)}
                        >
                          <FontAwesomeIcon
                            icon={faGamepadAltDuotone}
                            className='text-darkest size-10 group-disabled:text-slate-400 md:size-10'
                            style={
                              {
                                '--fa-secondary-color':
                                  (context.stateStores.gamepadState.playerMappings[playerId ?? '']?.gamepadIndexes ?? []).length > 0
                                    ? '#00d492'
                                    : '#2D618C',
                                '--fa-secondary-opacity': '1',
                              } as React.CSSProperties
                            }
                          />
                        </button>
                        <KeyboardMouseButton
                          id={`mobile-keyboard-mouse-button-${playerId}`}
                          isKeyboardEnabled={isKeyboardEnabled}
                          isMouseEnabled={isMouseEnabled}
                          onClick={() => setMobileKeyboardAndMouseExpanded(true)}
                        />
                        <button
                          data-testid={`mobile-pause-button-${name}`}
                          disabled={context.stateStores.pauseInputState.paused}
                          className={clsx(
                            'group flex size-12 items-center justify-center rounded-lg p-2',
                            context.stateStores.pauseInputState.paused ? 'bg-slate-600' : 'bg-primary active:bg-primary/80',
                          )}
                          onClick={handlePauseToggle}
                        >
                          <FontAwesomeIcon
                            icon={isPaused ? faPlay : faPause}
                            className='text-darkest size-8 group-disabled:text-slate-400 md:size-10'
                          />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {!mobileGamepadExpanded && (
                  <div className='border-primary bg-primary flex h-full items-center border-l-2'>
                    <button
                      data-testid={`mobile-kick-button-${name}`}
                      className='h-full w-12 bg-orange-600/60 p-2 active:bg-orange-600/40'
                      onClick={handleKickToggle}
                    >
                      <FontAwesomeIcon icon={faBootHeeled} className='size-6 -rotate-[30deg] text-orange-600 md:size-8' />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {isGlobal && (
        <div className='hidden flex-col gap-1 lg:flex'>
          <button
            data-testid='kick-all-button'
            disabled={players.length === 0}
            className='bg-primary hover:bg-primary/80 active:bg-primary/60 group flex items-center gap-3 rounded px-4 py-1 disabled:bg-slate-600 disabled:text-slate-400'
            onClick={handleKickAll}
          >
            <FontAwesomeIcon icon={faBootHeeled} className='size-4 -rotate-[30deg] text-orange-600 group-disabled:text-slate-400' />
            <span>Kick All</span>
          </button>
          <button
            data-testid='pause-all-button'
            className='bg-primary hover:bg-primary/80 active:bg-primary/60 flex items-center gap-2 rounded px-3 py-1'
            onClick={handlePauseAll}
          >
            <FontAwesomeIcon icon={context.stateStores.pauseInputState.paused ? faPlay : faPause} className='size-6 text-amber-400' />
            <span>{context.stateStores.pauseInputState.paused ? 'Unpause All' : 'Pause All'}</span>
          </button>
          <PlayerInputOptionsV2 />
        </div>
      )}
    </>
  );
};

export const Controller = () => {
  const context = useContext(PlaycastContext);
  const [players, setPlayers] = useState<Player[]>([]);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Memoize the player list update
  const updatePlayers = useCallback(() => {
    const playerState = context.stateStores.playerState;
    const out = Object.entries(playerState.players).map(([playerId, player]) => ({
      playerId,
      name: player.displayName,
    }));
    setPlayers(out);
  }, [context.stateStores.playerState]);

  // Memoize the scrollbar check
  const checkForScrollbar = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setHasScrollbar(container.scrollHeight > container.clientHeight);
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkForScrollbar();

    const resizeObserver = new ResizeObserver(checkForScrollbar);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkForScrollbar]);

  useEffect(() => {
    updatePlayers();
  }, [updatePlayers]);

  const scrollbarClasses = useMemo(
    () =>
      clsx(
        'h-full space-y-2 overflow-y-auto',
        hasScrollbar ? 'pr-2' : '',
        '[&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-slate-600',
        '[&::-webkit-scrollbar-track]:rounded-md [&::-webkit-scrollbar-track]:bg-slate-800',
        '[&::-webkit-scrollbar-track]:shadow-[inset_0_0_0_2px_rgb(var(--colors-primary))]',
        '[&::-webkit-scrollbar]:w-8 [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:bg-slate-800',
      ),
    [hasScrollbar],
  );

  // Memoize the controller panel list
  const controllerPanels = useMemo(
    () => (
      <>
        {/* {[
          Array.from({ length: 10 }).map((_, index) => (
            <ControllerPanel key={index} name={`Player ${index + 1}`} playerId={`player${index + 1}`} />
          )),
        ]} */}
        {players.map((player) => (
          <ControllerPanel key={player.playerId} name={player.name} playerId={player.playerId} />
        ))}
        <InviteButton
          text='Invite Your Friends To Join'
          icon={faUserPlus}
          tooltip={
            context.stateStores.hostState.status === 'streaming'
              ? !(
                  context.stateStores.invitationStateV2.type === 'lobby' &&
                  context.stateStores.invitationStateV2.manifest.lobbyOptions.canJoin
                )
                ? 'Friends cannot join when lobby is closed'
                : undefined
              : 'Start a session to invite friends!'
          }
        />
      </>
    ),
    [
      players,
      context.stateStores.hostState.status,
      context.stateStores.invitationStateV2.type,
      context.stateStores.invitationStateV2.manifest,
    ],
  );

  return (
    <>
      <div className='flex gap-2'>
        <ControllerPanel name='Host Controls' />
      </div>
      <div className='border-primary bg-darker h-[317px] overflow-hidden rounded-lg border-2 md:h-[380px] lg:h-full lg:flex-1'>
        <div className='h-full p-2'>
          <div ref={scrollContainerRef} className={scrollbarClasses}>
            {controllerPanels}
          </div>
        </div>
      </div>
    </>
  );
};
